<script setup>
import * as d3 from 'd3';
import * as d3hexbin from 'd3-hexbin';
import { onMounted, toRaw } from 'vue';

const props = defineProps({
    items: {
        type: Array,
        required: true,
        default: () => [],
    },
    id: {
        type: String,
        required: true,
        default: () => Math.random().toString(36).substring(7),
    }
});

const directions = [
    [1, 0], // Right
    [0.5, Math.sqrt(3) / 2], // Bottom right
    [-0.5, Math.sqrt(3) / 2], // Bottom left
    [-1, 0], // Left
    [-0.5, -Math.sqrt(3) / 2], // Top left
    [0.5, -Math.sqrt(3) / 2]  // Top right
];

const getRandomDirection = () => {
    return Math.floor(Math.random() * directions.length);
};

const build = (items, clear = false) => {
    let d3Hexbin = d3hexbin.hexbin();

    let formatHexes = [];
    items.forEach((item) => {
        formatHexes.push({
            color: item.color,
        });
    });

    let sortHexesByDate = formatHexes.sort((a, b) => new Date(a.date) - new Date(b.date));

    let hexes = sortHexesByDate;

    let count = hexes.length;

    // Create an SVG element
    const svg = d3.select('#kale-' + props.id);
    if (clear) {
        svg.selectAll('*').remove();
    }

    // Define the dimensions of the hexagon
    const width = document.querySelector('#kale-wrapper').offsetWidth - 25;
    const height = 400;
    let hexRadius = 15;

    if (count >= 100 && count < 250) {
        hexRadius = 12;
    } else if (count >= 250 && count < 400) {
        hexRadius = 10;
    } else if (count >= 400 && count < 600) {
        hexRadius = 8;
    } else if (count >= 600 && count < 800) {
        hexRadius = 5;
    } else if (count >= 800 && count < 1000) {
        hexRadius = 3;
    } else {
        hexRadius = 25;
    }

    // Create the hexbin generator
    const hexbin = d3Hexbin
        .radius(hexRadius);

    // Center hexagon (placed in the middle)
    const centerX = width / 2;
    const centerY = (height / 2) - 100;

    // Directions for hexagon movement: [x offset, y offset]

    // Function to generate a spiral of hexagons
    function generateRandomHexagons(hexes) {
        const points = [];

        // Add the center hexagon
        points.push([centerX, centerY]);

        // Keep track of how many hexagons we have added
        let currentHexCount = 1;

        // Loop through layers and add hexagons
        let layer = 1;
        hexes.forEach((_) => {
            let x = centerX;
            let y = centerY;

            // Traverse each layer, placing hexagons in random directions
            for (let direction = 0; direction < 6; direction++) {
                for (let i = 0; i < layer; i++) {
                    if (currentHexCount >= hexes.length + 1) {
                        return points; // Stop when we reach the desired count
                    }

                    // Randomly select a direction for each hexagon
                    const randomDirection = getRandomDirection();

                    // Calculate the new position based on the random direction
                    x += directions[randomDirection][0] * hexRadius * Math.sqrt(3);
                    y += directions[randomDirection][1] * hexRadius * Math.sqrt(3);

                    // Add the hexagon to the points array
                    points.push([x, y, {
                        layer: layer,
                        direction: randomDirection,
                        index: currentHexCount,
                        color: hexes[currentHexCount - 1]?.color ?? null,
                        date: hexes[currentHexCount - 1]?.date,
                    }]);

                    currentHexCount++;
                }
            }

            layer++;  // Move to the next layer
        });

        return points;
    }

    const hexPoints = generateRandomHexagons(hexes);

    // Create a group for hexagons and labels
    const g = svg.append('g');

    // Draw hexagons
    g.append('g')
        .selectAll('.hexagon')
        .data(hexPoints)
        .enter().append('path')
        .attr('d', hexbin.hexagon())  // generate hexagon path
        .attr('transform', d => `translate(${d[0]}, ${d[1]})`)
        .attr('stroke', '#000')  // Optional: set stroke color
        .attr('stroke-width', 1)  // Optional: set stroke width
        .attr('fill', (d) => {
            return d?.[2]?.color ?? '#5d336e'; // Set center fill color
        })
        .style('opacity', 0)  // Initially hide the hexagons
        .transition()  // Start the transition
        .delay((d, i) => i * 200)  // Delay each hexagon by 100ms (adjust as needed)
        .duration(500)  // Set the animation duration (500ms for each hexagon)
        .style('opacity', 1);  // Fade in the hexagons
};

onMounted(() => {
    let val = toRaw(props.items);
    if (val) {
        build(val);
    }
});
</script>

<template>
<div id="kale-wrapper">
    <svg
        :id="'kale-' + id"
        width="100%"
        height="200px">
        t
    </svg>
</div>
</template>
